import { useCallback, useEffect, useRef, useState } from "react";

/** Resolved by `run` when its call was cancelled or superseded. */
export const ABORTED = Symbol("aone.load.aborted");

/**
 * Tracks one list fetch at a time.
 *
 *   const list = useAbortableLoad();
 *   const res = await list.run((signal) => getThings(params));
 *   if (res === ABORTED) return;
 *
 * `run` hands the callback an AbortSignal (pass it to axios/fetch where the
 * service accepts one). Either way a cancelled or superseded call resolves to
 * ABORTED, so its response never lands, and only the latest call can clear
 * `loading`.
 */
export function useAbortableLoad() {
  const [loading, setLoading] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const seq = useRef(0);
  const controller = useRef(null);

  const run = useCallback(async (fn) => {
    controller.current?.abort();
    const ctrl = new AbortController();
    controller.current = ctrl;
    const id = ++seq.current;
    setLoading(true);
    setCancelled(false);
    try {
      const result = await fn(ctrl.signal);
      return id === seq.current && !ctrl.signal.aborted ? result : ABORTED;
    } catch (err) {
      if (id !== seq.current || ctrl.signal.aborted) return ABORTED;
      throw err;
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, []);

  const cancel = useCallback(() => {
    controller.current?.abort();
    seq.current += 1;
    setLoading(false);
    setCancelled(true);
  }, []);

  // Drop any in-flight response once the page unmounts.
  useEffect(() => () => {
    controller.current?.abort();
    seq.current += 1;
  }, []);

  // Like `run`, but a cancelled or superseded call never settles, so the
  // caller's own `.then` / `await` code simply never sees the stale response.
  // Lets existing fetch code be tracked without adding ABORTED checks.
  const track = useCallback(
    (fn) => run(fn).then((result) => (result === ABORTED ? new Promise(() => {}) : result)),
    [run]
  );

  return { loading, cancelled, run, track, cancel, setCancelled };
}

export default useAbortableLoad;

/** Props for a table shell (PaginationMainTable, MainTable, …) from a load. */
export function tableLoadingProps(load, loadingLabel) {
  return {
    loading: load.loading,
    loadingLabel,
    onCancelLoading: load.cancel,
    cancelled: load.cancelled,
  };
}
